import React, { useState, useEffect, useRef } from "react";
import { Chess } from "chess.js";
import { Chessboard } from "react-chessboard";
import { sendMessage } from "../servises/websocket";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import AvatarsGrid from "./AvatarsGrid";

const ChessBoard = ({ generalMessage, onGoToTimeSelection, updateBackground  }) => {
  const [game] = useState(new Chess());
  const [fen, setFen] = useState(game.fen());
  const [playerColor, setPlayerColor] = useState(null);
  const [gameId, setGameId] = useState(null);
  const [gameOverMessage, setGameOverMessage] = useState(null);
  const [showBlink, setShowBlink] = useState(false);
  const [playerTime, setPlayerTime] = useState(0);
  const [opponentTime, setOpponentTime] = useState(0);
  const [gameOver, setGameOver] = useState(false); // Track game over state
  const [bestMove, setBestMove] = useState(null); 
  const [userLastMove, setUserLastMove] = useState(null);
  const [generalMessageInit, setGeneralMessageInit] = useState(null);
  const [generalMessageTeamStatus, setGeneralMessageTeamStatus] = useState(null);

  const stockfishRef = useRef(null);

  useEffect(() => {

     // Example: Change background when the component is mounted
    updateBackground({
      image: `${process.env.PUBLIC_URL}/images/back_ground.avif`, // Replace with the desired background
      size: "cover",
      position: "center",
    });
    // Initialize Stockfish Web Worker
    //let url = `${process.env.PUBLIC_URL}/stockfish.js`
    //stockfishRef.current = new Worker(url);
    stockfishRef.current = new Worker(`${process.env.PUBLIC_URL}/stockfish.wasm.js`);

    stockfishRef.current.onmessage = (event) => {
      const message = event.data;
      //console.log("Stockfish message:", message);

      if (message.startsWith("bestmove")) {
        const bestMove = message.split(" ")[1];
        
        setBestMove(bestMove);
        
      }
    };

    return () => {
      // Reset background to default when ChessBoard is unmounted
      updateBackground({
        image: `${process.env.PUBLIC_URL}/images/background_1_1.jfif`,
        size: "cover",
        position: "center",
      });

      if (stockfishRef.current) {
        stockfishRef.current.terminate();
      }
    };
  }, [updateBackground]);

  useEffect(() => {
    if (generalMessage) {
      if (generalMessage.messageType === "init_game") {
        setGeneralMessageInit(generalMessage);
        setPlayerColor(generalMessage.color);
        setGameId(generalMessage.game.gameId);
        const initialFen = generalMessage.game.initialFen || game.fen(); // Fallback to default
        const fullFen = `${initialFen}`; 
        game.load(fullFen);
        setFen(game.fen());

        analyzePosition(game.fen());

        console.log("Game initialized with FEN:", fullFen);
        console.log("Player color received:", generalMessage.color);
      }

      if (
        generalMessage.messageType === "server_move" &&
        generalMessage.move.commandFen
      ) {
        console.log("Server move received:", generalMessage.move.commandFen);
        game.load(generalMessage.move.commandFen);
        
        analyzePosition(game.fen());
        setFen(game.fen());
      }

      if (
        generalMessage.messageType === "team_member_move"
      ) {
        console.log("Team move received");
        setGeneralMessageTeamStatus(generalMessage);
      }

      if (generalMessage.messageType === "game_over") {
        console.log("Game over");
        setGameOver(true);
        if(generalMessage.game.gameStatus === "draw"){
          setGameOverMessage("Draw is a decent outcome, but you can aim for even better!");
        }
        else if (generalMessage.game.winner === playerColor) {
          setGameOverMessage("Congratulations! You won the game!");
        } else {
          setGameOverMessage("You lost. Better luck next time!");
        }
        setShowBlink(true);

        setTimeout(() => {
          setShowBlink(false);
        }, 3000);
      }

      if (generalMessage.messageType === "update_time") {
        if (playerColor === generalMessage.color) {
          setPlayerTime(generalMessage.move.currentSeconds);
          setOpponentTime(generalMessage.move.opponentSeconds);
        } else {
          setPlayerTime(generalMessage.move.opponentSeconds);
          setOpponentTime(generalMessage.move.currentSeconds);
        }
      }
    }
  }, [generalMessage, game, playerColor]);

  const handleMove = (sourceSquare, targetSquare) => {
    
    if (
      (playerColor === "white" && game.turn() !== "w") ||
      (playerColor === "black" && game.turn() !== "b")
    ) {
      console.warn("It's not your turn!");
      return false;
    }

    const piece = game.get(sourceSquare); // Get the piece on the source square
    const isPawn = piece?.type === "p"; // Check if the piece is a pawn
    const isPromotionRank = (targetSquare[1] === "8" && game.turn() === "w") ||
                          (targetSquare[1] === "1" && game.turn() === "b"); // Check if move is to the promotion rank

    let move;
    try{
      move = game.move({
        from: sourceSquare,
        to: targetSquare,
        promotion: isPawn && isPromotionRank ? "q" : undefined, // Add promotion only for pawns
      });
    }catch(e){
      console.log('Error: ' + e);
    }                      

    if (move === null) {
      console.warn("Invalid move");
      return false;
    }
    
    const userMoveNotation = sourceSquare + targetSquare;
    setUserLastMove(userMoveNotation);

    //console.log("best move: ", bestMove);
    
    if (userMoveNotation === bestMove) {
      console.log("User made the best move:", bestMove);
    } else {
      console.log(`Best move: ${bestMove}, but user move: ${userMoveNotation}`);
    }

    setFen(game.fen());
    let gameStatus = checkGameOver();
    const generalMessage = { ...generalMessageTemplate };
    generalMessage.move.userMove = sourceSquare + targetSquare;
    generalMessage.move.bestMove = bestMove;
    generalMessage.move.userFen = game.fen();
    generalMessage.game.gameId = gameId;
    generalMessage.move.color = playerColor;
    generalMessage.messageType = "user_move";
    generalMessage.color = playerColor;
    generalMessage.game.gameStatus = gameStatus;
    generalMessage.move.gameStatus = gameStatus;
    
    // Send position to Stockfish for analysis
    //analyzePosition(game.fen()); - to analyze the opponent best move
  
    sendMessage(generalMessage);
    return true;
   
  };

  const analyzePosition = (fen) => {
    if (stockfishRef.current) {
      stockfishRef.current.postMessage(`position fen ${fen}`);
      stockfishRef.current.postMessage("go depth 15");
  
      stockfishRef.current.onmessage = (event) => {
        const message = event.data;
  
        if (message.startsWith("bestmove")) {
          const bestMove = message.split(" ")[1];
          setBestMove(bestMove);
        }
      };
    }
  };
  
  const checkGameOver = () => {
    let ret = "in_progress";
    if (game.isCheckmate()) {
      const winner = game.turn() === "w" ? "Black" : "White";
      if(winner === "White"){
        ret = "white_won_by_mat_black";
      }
      else{
        ret = "black_won_by_mat_white";
      }
    }
    else if (game.isStalemate()) {
      ret = "stalemate";
    }
    else if (game.isThreefoldRepetition()) {
      ret = "threefold_repetition";
    }  
    else if (game.isInsufficientMaterial()) {
      ret = "insufficient_material";
    }
    else if (game.isCheck()) {
      ret = "check";
    }  
    else if (game.isDraw()) {
      ret = "draw";
    }

    return ret;
  };

  const handleLeaveGame = () => {
    // Notify the server about leaving the game
    const leaveMessage = { ...generalMessageTemplate };
    leaveMessage.messageType = "leave_game";
    leaveMessage.game.gameId = gameId;
    leaveMessage.color = playerColor;

    sendMessage(leaveMessage);

    // Trigger the parent component to navigate to game selection
    onGoToTimeSelection();
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

 

return (
  <div
    style={{
      textAlign: "center",
      marginTop: "20px",
      backgroundColor: "white", // White background
      padding: "100px", // Add padding for spacing
      borderRadius: "50px", // Rounded corners for a modern look
      
      maxWidth: "800px", // Limit width to resemble a centered container
      margin: "auto", // Center horizontally
    }}
  >
    {!gameOver && (
      <div style={{ marginBottom: "15px" }}>
        <strong>Opponent's Time:</strong> {formatTime(opponentTime)}
      </div>
    )}

    <div style={{ display: "inline-block", position: "relative" }}>

      {/* Opponent Avatars (Above the Chessboard) */}
      <div
        style={{
          position: "absolute",
          top: -120, // Adjust the distance from the top (above the board)
          left: "50%",
          transform: "translateX(-50%)", // Center horizontally
          zIndex: 10, // Ensure it appears above the chessboard
        }}
      >
         <AvatarsGrid
          avatars={generalMessageTeamStatus?.game[playerColor === "white" ? "blackTeam" : "whiteTeam"] || []}
          position="top"
          
        />
      </div>

      {/* User Avatars (Below the Chessboard) */}
      <div
        style={{
          position: "absolute",
          bottom: -140, // Adjust distance from the chessboard
          left: "50%",
          transform: "translateX(-50%)", // Center horizontally
          zIndex: 10, // Ensure it appears above the chessboard
        }}
      >
         <AvatarsGrid
          avatars={generalMessageTeamStatus?.game[playerColor === "white" ? "whiteTeam" : "blackTeam"] || []}
          position="bottom"
        />
      </div>

      {/* Chessboard with Labels */}
      <div style={{ display: "grid", gridTemplateColumns: "20px auto", gap: "0" }}>
        {/* Row Labels */}
        <div style={{ display: "grid", gridTemplateRows: "repeat(8, 1fr)", alignItems: "center" }}>
          {Array.from({ length: 8 }, (_, i) => (
            <div
              key={`row-label-${i}`}
              style={{
                textAlign: "center",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              {playerColor === "white" ? 8 - i : i + 1}
            </div>
          ))}
        </div>

        {/* Chessboard */}
        <div style={{ position: "relative" }}>
          <Chessboard
            position={fen}
            onPieceDrop={handleMove}
            boardWidth={350}
            boardOrientation={playerColor}
          />

          {/* Column Labels */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(8, 1fr)",
              position: "absolute",
              bottom: -20,
              width: "100%",
              textAlign: "center",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            {(playerColor === "white"
              ? ["a", "b", "c", "d", "e", "f", "g", "h"]
              : ["h", "g", "f", "e", "d", "c", "b", "a"]
            ).map((letter) => (
              <div key={`col-label-${letter}`}>{letter}</div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {gameOverMessage && (
      <div style={{ marginTop: "20px" }}>
        <p
          style={{
            fontWeight: "bold",
            animation: showBlink ? "blink 0.5s step-start infinite" : "none",
          }}
        >
          {gameOverMessage}
        </p>
      </div>
    )}

    {gameOver ? (
      <button
        style={{
          marginTop: "10px",
          padding: "10px 20px",
          fontSize: "16px",
          cursor: "pointer",
        }}
        onClick={onGoToTimeSelection}
      >
        Select the Game
      </button>
    ) : (
      <div style={{ marginTop: "20px" }}>
        <strong>Your Time:</strong> {formatTime(playerTime)}
      </div>
    )}

    {!gameOver && (
      <button
        onClick={handleLeaveGame} // Handle leave game
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          backgroundColor: "white",
          border: "1px solid #ccc",
          borderRadius: "50%",
          width: "40px",
          height: "40px",
          fontSize: "20px",
          cursor: "pointer",
        }}
        title="Exit Game"
      >
        &#x2715; {/* X icon */}
      </button>
    )}

    <style>
      {`
        @keyframes blink {
          50% {
            opacity: 0;
          }
        }
      `}
    </style>
  </div>
);

  
  
};

export default ChessBoard;
